import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Captions, Trash2, Play, Loader2, AlertTriangle, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg, fmtTime } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { useTitle } from "../lib/seo";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Videos() {
  useTitle("Mis videos · AR Studio Creative");
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [fmt, setFmt] = useState("all");
  const [play, setPlay] = useState(null);
  const { user } = useAuth();
  const toggleSample = async (id) => { const { data } = await api.post(`/admin/videos/${id}/sample`); toast.success(data.sample ? "Publicado como muestra en la portada" : "Quitado de la portada"); load(); };

  const load = () => api.get("/videos").then((r) => setItems(r.data.filter((v) => v.status !== "deleted")));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!items?.some((v) => ["queued", "processing"].includes(v.status))) return;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [items]);

  const del = async (id) => {
    try { await api.delete(`/videos/${id}`); toast.success("Video eliminado"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const list = (items || []).filter((v) => v.title.toLowerCase().includes(q.toLowerCase()) && (fmt === "all" || v.aspect === fmt));

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="videos-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="flex flex-wrap gap-4 items-end justify-between">
          <div><h1 className="text-3xl font-extrabold text-white">Mis videos</h1><p className="text-zinc-400 text-sm mt-1">Descarga tus MP4 y subtítulos SRT.</p></div>
          <div className="flex flex-wrap gap-2">
            <Input data-testid="videos-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por título" className="bg-zinc-900/60 w-56" />
            {[["all", "Todos"], ["16:9", "16:9"], ["9:16", "9:16"]].map(([k, l]) => (
              <Button key={k} variant="outline" data-testid={`videos-filter-${k.replace(":", "x")}`} onClick={() => setFmt(k)} className={`border-white/10 ${fmt === k ? "bg-[#E8C47C]/20 text-white" : ""}`}>{l}</Button>
            ))}
          </div>
        </div>
        {items === null ? <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#E8C47C]" /></div> :
          list.length === 0 ? (
            <div className="panel mt-10 p-16 text-center" data-testid="videos-empty">
              <p className="text-zinc-400">Aún no tienes videos.</p>
              <Link to="/studio"><Button className="mt-6 rounded-full bg-[#E8C47C] hover:bg-[#F3D9A4] text-black font-semibold" data-testid="videos-create-btn"><Plus className="h-4 w-4 mr-2" />Crear video</Button></Link>
            </div>
          ) : (
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {list.map((v) => (
                <div key={v.id} className="panel overflow-hidden group fade-up" data-testid={`video-card-${v.id}`}>
                  <div className="relative aspect-video bg-zinc-900 grid place-items-center overflow-hidden">
                    {v.status === "done" ? (
                      <>
                        <img src={`${API}/videos/${v.id}/thumb`} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <button onClick={() => setPlay(v)} data-testid={`video-play-${v.id}`} className="absolute inset-0 grid place-items-center bg-black/0 hover:bg-black/40 transition-colors">
                          <span className="h-12 w-12 rounded-full bg-[#E8C47C]/90 text-black grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"><Play className="h-5 w-5 text-white" /></span>
                        </button>
                      </>
                    ) : v.status === "failed" ? <div className="text-red-300 text-xs flex gap-2 px-4" data-testid={`video-error-${v.id}`}><AlertTriangle className="h-4 w-4 shrink-0" />{v.error?.slice(0, 120)}</div> :
                      <div className="w-3/4 space-y-2"><Progress value={v.progress} className="h-1.5" /><p className="text-xs text-zinc-400 text-center">{v.step} · {v.progress}%</p></div>}
                    <span className="absolute top-2 left-2 text-[10px] font-mono-tc bg-black/70 px-2 py-0.5 rounded">{v.aspect}</span>
                    {(v.resolution || v.height) && <span data-testid={`video-version-${v.id}`} className="absolute top-2 right-2 text-[10px] font-mono-tc bg-[#E8C47C] text-black px-2 py-0.5 rounded">{v.resolution || Math.min(v.width, v.height)}p · Versión {v.plan_version || "—"}</span>}
                    {v.duration && <span className="absolute bottom-2 right-2 text-[10px] font-mono-tc bg-black/70 px-2 py-0.5 rounded">{fmtTime(v.duration)}</span>}
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-white font-medium truncate">{v.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{new Date(v.created_at).toLocaleString("es")}</p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {v.status === "done" && <a href={`${API}/videos/${v.id}/file?download=1`} data-testid={`video-download-${v.id}`}><Button size="sm" variant="outline" className="border-white/10 h-8"><Download className="h-3.5 w-3.5 mr-1" />MP4</Button></a>}
                      {v.has_srt && <a href={`${API}/videos/${v.id}/srt`} data-testid={`video-srt-${v.id}`}><Button size="sm" variant="outline" className="border-white/10 h-8"><Captions className="h-3.5 w-3.5 mr-1" />SRT</Button></a>}
                      {user?.role === "admin" && v.status === "done" && <Button size="sm" variant="outline" data-testid={`video-sample-${v.id}`} onClick={() => toggleSample(v.id)} className={`h-8 border-white/10 ${v.sample ? "text-[#E8C47C]" : ""}`}>{v.sample ? "★ Muestra" : "☆ Muestra"}</Button>}
                      <Button size="sm" variant="ghost" className="h-8 ml-auto" data-testid={`video-delete-${v.id}`} onClick={() => del(v.id)}><Trash2 className="h-3.5 w-3.5 text-zinc-500" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
      <Dialog open={!!play} onOpenChange={(o) => !o && setPlay(null)}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-3xl" data-testid="video-player-dialog">
          <DialogHeader><DialogTitle className="text-white">{play?.title}</DialogTitle></DialogHeader>
          {play && <video src={`${API}/videos/${play.id}/file`} controls autoPlay className={`mx-auto rounded-xl bg-black ${play.aspect === "9:16" ? "h-[70vh]" : "w-full"}`} />}
          {play?.credits?.length > 0 && <p className="text-[11px] text-zinc-500">Clips: {play.credits.map((c) => c.author).join(", ")} · Pexels</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
